import type {
  FaireAcademyChapter,
  FaireAcademyGuide,
} from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { convertTitleToHash } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/utils";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import { IFaireAcademyGuide } from "@faire/web-api--source/indigofair/data/IFaireAcademyGuide";
import layTheGroundwork from "@faire/web-api--source/routes/www/open-with-faire/academy/lay-the-groundwork";
import { lazy } from "react";
import * as React from "react";

const LazyYourConceptAndVision = lazy(() =>
  import(
    /* webpackChunkName: "lay-the-groundwork-your-concept-and-vision" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/LayTheGroundwork/__internal__/YourConceptAndVision"
  ).then((m) => ({ default: m.YourConceptAndVision }))
);
const LazyFindingYourSupportNetwork = lazy(() =>
  import(
    /* webpackChunkName: "lay-the-groundwork-finding-your-support-network" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/LayTheGroundwork/__internal__/FindingYourSupportNetwork"
  ).then((m) => ({ default: m.FindingYourSupportNetwork }))
);
const LazyWritingABusinessPlan = lazy(() =>
  import(
    /* webpackChunkName: "lay-the-groundwork-writing-a-business-plan" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/LayTheGroundwork/__internal__/WritingABusinessPlan"
  ).then((m) => ({ default: m.WritingABusinessPlan }))
);

const C1 = "Your concept and vision";
const YOUR_CONCEPT_AND_VISION: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.YOUR_CONCEPT_AND_VISION,
  title: C1,
  readTimeMinutes: 1,
  titleHash: convertTitleToHash(C1),
  ChapterComponent: LazyYourConceptAndVision,
};

const C2 = "Finding your support network";
const FINDING_YOUR_SUPPORT_NETWORK: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.FINDING_YOUR_SUPPORT_NETWORK,
  title: C2,
  readTimeMinutes: 1,
  titleHash: convertTitleToHash(C2),
  ChapterComponent: LazyFindingYourSupportNetwork,
};

const C3 = "Writing a business plan";
const WRITING_A_BUSINESS_PLAN: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.WRITING_A_BUSINESS_PLAN,
  title: C3,
  readTimeMinutes: 6,
  titleHash: convertTitleToHash(C3),
  ChapterComponent: LazyWritingABusinessPlan,
};

const GUIDE_CHAPTERS: Array<FaireAcademyChapter> = [
  YOUR_CONCEPT_AND_VISION,
  FINDING_YOUR_SUPPORT_NETWORK,
  WRITING_A_BUSINESS_PLAN,
];

export const LAY_THE_GROUNDWORK: FaireAcademyGuide = {
  id: IFaireAcademyGuide.GuideIdentifier.LAY_THE_GROUNDWORK,
  title: "Lay the groundwork",
  description:
    "Create a business plan that maps your vision for your retail store.",
  cardSummary: "Map out your vision and create a business plan to get started.",
  guideSummary: (
    <>
      Starting a new retail business begins with careful planning and
      decision-making. First and foremost, you'll need a robust business plan.
      This should include a high-level description of your business, its
      mission, and your products and services. You'll also want to conduct a
      market analysis, select the right business structure (e.g., sole
      proprietorship or LLC), and outline your business goals. By establishing a
      clear and structured business plan, you lay a strong foundation for your
      retail venture, setting the stage for sustainable growth and success.
    </>
  ),
  featuredImage:
    "https://cdn.faire.com/fastly/3f4e6a697635702d18b65e8d58ac56061ba4798c779c66307b7309ba537e0def.png",
  featuredImageAltText: "Store owner in blue jacket planning her new business",
  backgroundImage:
    "https://cdn.faire.com/fastly/62b903b61ef03553dceed58885573e375c56834f6d58ad859baf0d6121dd7921.png",
  readTimeMinutes: GUIDE_CHAPTERS.reduce(
    (partialReadTime, chapter) => partialReadTime + chapter.readTimeMinutes,
    0
  ),
  chapters: GUIDE_CHAPTERS,
  linkToPage: layTheGroundwork(),
  titleTag: "Creating a business plan for your new retail store",
};
